import { useMemo } from "react";
import { Link } from "react-router-dom";
import Badge from "@/components/common/Badge";
import ActionMenu from "@/components/common/ActionMenu";
import DataTable, { DATA_TABLE_CLASSES } from "@/components/common/DataTable";
import RowIcon from "@/components/common/RowIcon";
import FillBar from "@/components/common/FillBar";
import AnimatedNumber from "@/components/common/AnimatedNumber";
import { DEFAULT_CAMPAIGN_ICON, nameOr } from "@/utils/itemIcon";
import { getRowIcon } from "@/utils/getRowIcon";
import { Pencil, Trash2 } from "lucide-react";

const STATUS_VARIANT = {
  active: "success",
  completed: "info",
  paused: "warn",
  draft: "neutral",
  pending: "neutral",
};

const DATE_FORMAT = { month: "short", day: "numeric", year: "numeric" };

const initialsOf = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const formatDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, DATE_FORMAT);
};

const formatCurrency = (value) =>
  `$${(Number(value) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatNumber = (n) => (n ?? 0).toLocaleString("en-US");

export default function CampaignsTable({
  campaigns,
  users = [],
  segments = [],
  stats = {},
  loadingUuids,
  showAuthor = false,
  onEdit,
  onDelete,
  emptyMessage = "No campaigns to display.",
}) {
  const usersById = useMemo(() => {
    const map = new Map();
    for (const u of users) map.set(u.uuid, u);
    return map;
  }, [users]);

  const segmentsById = useMemo(() => {
    const map = new Map();
    for (const s of segments) map.set(s.uuid, s);
    return map;
  }, [segments]);

  const showActions = Boolean(onEdit || onDelete);
  const columnCount =
    1 /* name */ +
    (showAuthor ? 1 : 0) +
    7 /* status, timeframe, segments, impressions, devices, budget, creative */ +
    (showActions ? 1 : 0);

  return (
    <DataTable>
      <thead>
        <tr className={DATA_TABLE_CLASSES.headRow}>
          <th className={DATA_TABLE_CLASSES.th}>Name</th>
          {showAuthor && <th className={DATA_TABLE_CLASSES.th}>Author</th>}
          <th className={DATA_TABLE_CLASSES.th}>Status</th>
          <th className={DATA_TABLE_CLASSES.th}>Timeframe</th>
          <th className={DATA_TABLE_CLASSES.th}>Segments</th>
          <th className={`${DATA_TABLE_CLASSES.th} w-[200px]`}>Impressions</th>
          <th className={DATA_TABLE_CLASSES.th}>Devices</th>
          <th className={`${DATA_TABLE_CLASSES.th} w-[200px]`}>Budget used</th>
          <th className={DATA_TABLE_CLASSES.th}>Creative</th>
          {showActions && (
            <th className={DATA_TABLE_CLASSES.thRight}>Actions</th>
          )}
        </tr>
      </thead>
      <tbody>
        {campaigns.length === 0 ? (
          <tr>
            <td colSpan={columnCount} className={DATA_TABLE_CLASSES.emptyCell}>
              {emptyMessage}
            </td>
          </tr>
        ) : (
          campaigns.map((campaign) => {
            const parsed = getRowIcon(campaign, DEFAULT_CAMPAIGN_ICON);
            const Icon = parsed.Icon;
            const displayName = nameOr(parsed.displayName, "Untitled campaign");
            const rowStats = stats[campaign.uuid];
            const isLoading =
              (loadingUuids && loadingUuids.has(campaign.uuid)) ||
              rowStats === undefined;
            const impressions = Number(rowStats?.totalReceivers ?? 0);
            const devices = Number(rowStats?.uniqueReceivers ?? 0);
            const cpm = Number(campaign.cpm ?? 0);
            const budgetUsed = (impressions * cpm) / 1000;
            const budgetCap = Number(campaign.budgetCap ?? 0);
            const impressionsCap = Number(campaign.impressionsCap ?? 0);
            const author = campaign.userId
              ? usersById.get(campaign.userId)
              : null;
            const authorName = author?.username ?? campaign.createdBy ?? "—";
            const animated = campaign.status !== "completed";
            return (
              <tr key={campaign.uuid} className={DATA_TABLE_CLASSES.bodyRow}>
                <td className={DATA_TABLE_CLASSES.td}>
                  <div className="flex items-center gap-[13px]">
                    <RowIcon icon={Icon} />
                    <span className="text-[13.5px] font-bold text-(--text-primary)">
                      {displayName}
                    </span>
                  </div>
                </td>
                {showAuthor && (
                  <td className={DATA_TABLE_CLASSES.td}>
                    {authorName === "—" ? (
                      <span className="text-(--text-muted)">—</span>
                    ) : (
                      <span className="inline-flex items-center gap-2 rounded-(--radius-pill) bg-(--surface-2) py-1 pl-1 pr-3">
                        <span className="flex h-6 w-6 items-center justify-center rounded-(--radius-pill) bg-(--brand-primary) text-[10px] font-bold text-(--brand-primary-contrast)">
                          {initialsOf(authorName)}
                        </span>
                        <span className="text-[12.5px] font-semibold text-(--text-primary)">
                          {authorName}
                        </span>
                      </span>
                    )}
                  </td>
                )}
                <td className={DATA_TABLE_CLASSES.td}>
                  <Badge variant={STATUS_VARIANT[campaign.status] ?? "neutral"}>
                    {campaign.status}
                  </Badge>
                </td>
                <td className={DATA_TABLE_CLASSES.td}>
                  {(() => {
                    const start = formatDate(campaign.startDate);
                    const end = formatDate(campaign.endDate);
                    if (!start && !end) {
                      return <span className="text-(--text-muted)">—</span>;
                    }
                    return (
                      <div className="text-[12.5px] leading-tight text-(--text-primary)">
                        <div>{start || "—"}</div>
                        <div className="text-(--text-muted)">
                          {end ? `→ ${end}` : "→ no end"}
                        </div>
                      </div>
                    );
                  })()}
                </td>
                <td className={DATA_TABLE_CLASSES.td}>
                  {(() => {
                    const ids = Array.isArray(campaign.selectedSegments)
                      ? campaign.selectedSegments
                      : [];
                    if (ids.length === 0) {
                      return <span className="text-(--text-muted)">—</span>;
                    }
                    return (
                      <div className="flex max-w-[240px] flex-col text-[12.5px] leading-tight">
                        {ids.map((id) => {
                          const seg = segmentsById.get(id);
                          const label = seg?.name || `${id.slice(0, 6)}…`;
                          return (
                            <Link
                              key={id}
                              to={`/dashboard/segments/${id}/edit`}
                              className="block truncate text-(--text-primary) no-underline hover:underline"
                              title={seg?.name || id}
                            >
                              {label}
                            </Link>
                          );
                        })}
                      </div>
                    );
                  })()}
                </td>
                <td className={DATA_TABLE_CLASSES.td}>
                  <div className="w-[180px]">
                    <div className="text-[13.5px] font-bold text-(--text-primary)">
                      {isLoading ? (
                        <span className="text-(--text-muted)">…</span>
                      ) : (
                        <>
                          <AnimatedNumber value={impressions} />
                          {impressionsCap > 0 && (
                            <span className="ml-1 text-[11.5px] font-medium text-(--text-muted)">
                              / {formatNumber(impressionsCap)}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    {!isLoading && (
                      <FillBar
                        used={impressions}
                        cap={impressionsCap}
                        animated={animated}
                      />
                    )}
                  </div>
                </td>
                <td className={DATA_TABLE_CLASSES.td}>
                  <div className="text-[13.5px] font-bold text-(--text-primary)">
                    {isLoading ? (
                      <span className="text-(--text-muted)">…</span>
                    ) : (
                      <AnimatedNumber value={devices} />
                    )}
                  </div>
                </td>
                <td className={DATA_TABLE_CLASSES.td}>
                  <div className="w-[180px]">
                    <div className="text-[13.5px] font-bold text-(--text-primary)">
                      {isLoading ? (
                        <span className="text-(--text-muted)">…</span>
                      ) : (
                        <>
                          <AnimatedNumber
                            value={budgetUsed}
                            format={(n) => formatCurrency(n)}
                          />
                          {budgetCap > 0 && (
                            <span className="ml-1 text-[11.5px] font-medium text-(--text-muted)">
                              / {formatCurrency(budgetCap)}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    {!isLoading && (
                      <FillBar
                        used={budgetUsed}
                        cap={budgetCap}
                        animated={animated}
                      />
                    )}
                  </div>
                </td>
                <td className={DATA_TABLE_CLASSES.td}>
                  {Array.isArray(campaign.creatives) &&
                  campaign.creatives.length > 0 ? (
                    <div className="flex max-w-[240px] flex-col text-[12.5px] leading-tight">
                      {campaign.creatives.map((creative) => {
                        const label =
                          creative.name?.trim() ||
                          creative.url ||
                          creative.uuid;
                        return (
                          <Link
                            key={creative.uuid}
                            to={`/dashboard/creatives?edit=${creative.uuid}`}
                            className="block truncate text-(--text-primary) no-underline hover:underline"
                            title={creative.url || label}
                          >
                            {label}
                          </Link>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-(--text-muted)">No creative</span>
                  )}
                </td>
                {showActions && (
                  <td className={DATA_TABLE_CLASSES.tdRight}>
                    <ActionMenu
                      ariaLabel={`Actions for ${displayName}`}
                      items={[
                        onEdit && {
                          label: "Edit",
                          icon: Pencil,
                          onClick: () => onEdit(campaign),
                        },
                        onDelete && {
                          label: "Delete",
                          icon: Trash2,
                          danger: true,
                          onClick: () => onDelete(campaign),
                        },
                      ].filter(Boolean)}
                    />
                  </td>
                )}
              </tr>
            );
          })
        )}
      </tbody>
    </DataTable>
  );
}
